import { CardLicenseInventory } from "@/components/billing/card-license-inventory";
import { CardSubscriptionHistory } from "@/components/data-table-admin-teams/card-subscription-history";
import { LicenseCountSelector } from "@/components/setup/license-count-selector";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useAdminTeamsEmitter } from "@/hooks/useAdminTeamsEvents";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import {
  AdminTeamDTO,
  AdminTeamUserDTO,
  TeamInvitationDTO,
  TeamLicenseInventoryDTO,
  TeamSubscriptionHistoryEntryDTO,
  TeamUserDTO,
} from "@shaarp/common";
import { useCallback, useEffect, useState } from "react";
import { ButtonLoading } from "../button-loading";

/**
 * Lets an admin (re)define the starting Pro license plan of a team that hasn't
 * finished its setup yet. Only one frequency holds a count at a time — mirroring
 * team creation and the frequency lock enforced during the self-serve setup — so
 * switching frequency moves the count to the newly selected one. The values are
 * applied to the client's checkout when they finalize their subscription.
 */
const InitialLicensesEditor = ({
  team,
  onSaved,
}: {
  team: AdminTeamDTO;
  onSaved: () => void;
}) => {
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const { t } = useTranslation();
  const { refreshTeams } = useAdminTeamsEmitter();
  const prefix = (key: string) =>
    `admin.dataTableAdminTeams.dialogs.licenseInventory.editInitialLicenses.${key}`;

  const initialFrequency: "monthly" | "annual" =
    team.initialProLicenseCountAnnual != null ? "annual" : "monthly";
  const initialCount =
    (initialFrequency === "annual"
      ? team.initialProLicenseCountAnnual
      : team.initialProLicenseCountMonthly) ?? 1;
  const [frequency, setFrequency] = useState<"monthly" | "annual">(
    initialFrequency,
  );
  const [count, setCount] = useState<number>(initialCount);
  const [isLoading, setIsLoading] = useState(false);
  // Last persisted plan; the Save button stays disabled until the admin diverges
  // from it, so it can't re-submit an unchanged plan (which would log duplicate
  // history entries).
  const [savedPlan, setSavedPlan] = useState({
    frequency: initialFrequency,
    count: initialCount,
  });

  const isDirty =
    frequency !== savedPlan.frequency || count !== savedPlan.count;

  const save = async () => {
    try {
      setIsLoading(true);
      await api.post(`/admin/team/${team.id}/initial-licenses`, {
        frequency,
        count,
      });
      setSavedPlan({ frequency, count });
      refreshTeams();
      onSaved();
      toast({
        variant: "default",
        title: t(prefix("successTitle")),
        description: t(prefix("successDescription")),
      });
    } catch {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="space-y-5 p-6">
      <div className="space-y-1.5">
        <h2 className="text-base font-semibold leading-tight">
          {t(prefix("title"))}
        </h2>
        <p className="text-sm text-muted-foreground">{t(prefix("description"))}</p>
      </div>

      <Tabs
        value={frequency}
        onValueChange={(v) => setFrequency(v as "monthly" | "annual")}
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="monthly">{t(prefix("monthly"))}</TabsTrigger>
          <TabsTrigger value="annual">{t(prefix("annual"))}</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-muted-foreground">
          {t(prefix("countLabel"))}
        </span>
        <LicenseCountSelector value={count} min={1} onValueChange={setCount} />
      </div>

      <p className="text-xs text-muted-foreground">{t(prefix("helper"))}</p>

      <div className="flex justify-end">
        <ButtonLoading
          onClick={save}
          isLoading={isLoading}
          disabled={isLoading || !isDirty}
        >
          {t(prefix("save"))}
        </ButtonLoading>
      </div>
    </Card>
  );
};

/**
 * Lets an admin set the fixed license stock of a free team (billing disabled, no
 * Stripe customer). Licenses are monthly-only and admin-controlled: the manager
 * assigns these seats but cannot change the total. The count cannot be lowered
 * below the seats already assigned, as there is no scheduled-unassign flow
 * without a Stripe subscription.
 */
const FreePlanLicensesEditor = ({
  team,
  inventory,
  onSaved,
}: {
  team: AdminTeamDTO;
  inventory: TeamLicenseInventoryDTO;
  onSaved: (updated: TeamLicenseInventoryDTO) => void;
}) => {
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const { t } = useTranslation();
  const { refreshTeams } = useAdminTeamsEmitter();
  const prefix = (key: string) =>
    `admin.dataTableAdminTeams.dialogs.licenseInventory.freePlan.${key}`;

  const assignedPro = inventory.monthly.pro.assigned;
  const purchasedPro = inventory.monthly.pro.purchased;
  const [count, setCount] = useState<number>(purchasedPro);
  const [isLoading, setIsLoading] = useState(false);

  const save = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.post<TeamLicenseInventoryDTO>(
        `/admin/team/${team.id}/free-plan`,
        { proQuantity: count },
      );
      refreshTeams();
      onSaved(data);
      toast({
        variant: "default",
        title: t(prefix("successTitle")),
        description: t(prefix("successDescription")),
      });
    } catch {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="space-y-5 p-6">
      <div className="space-y-1.5">
        <h2 className="text-base font-semibold leading-tight">
          {t(prefix("title"))}
        </h2>
        <p className="text-sm text-muted-foreground">{t(prefix("description"))}</p>
      </div>

      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-muted-foreground">
          {t(prefix("countLabel"))}
        </span>
        <LicenseCountSelector
          value={count}
          min={assignedPro}
          onValueChange={setCount}
        />
      </div>

      <p className="text-xs text-muted-foreground">
        {t(prefix("assignedHelper"), { count: assignedPro })}
      </p>

      <div className="flex justify-end">
        <ButtonLoading onClick={save} isLoading={isLoading} disabled={isLoading}>
          {t(prefix("save"))}
        </ButtonLoading>
      </div>
    </Card>
  );
};

/**
 * Admin override on a billing-enabled team: pushes arbitrary quantities to
 * Stripe and resyncs. The team drives its own subscription, so this exists only
 * to recover from an anomaly (a webhook lost, a quantity changed straight in the
 * Stripe dashboard) that self-serve cannot undo.
 */
const QuantitiesOverrideEditor = ({
  team,
  inventory,
  onSaved,
}: {
  team: AdminTeamDTO;
  inventory: TeamLicenseInventoryDTO;
  onSaved: (updated: TeamLicenseInventoryDTO) => void;
}) => {
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const { t } = useTranslation();
  const { refreshTeams } = useAdminTeamsEmitter();
  const prefix = (key: string) =>
    `admin.dataTableAdminTeams.dialogs.licenseInventory.override.${key}`;

  const [frequency, setFrequency] = useState<"monthly" | "annual">("monthly");
  const [reason, setReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const freqData = inventory[frequency];
  const [proQuantity, setProQuantity] = useState(freqData.pro.purchased);

  // Switching frequency re-seeds the field from that pool, so the admin edits
  // the quantities actually in force rather than the other frequency's.
  const selectFrequency = (next: "monthly" | "annual") => {
    setFrequency(next);
    setProQuantity(inventory[next].pro.purchased);
  };

  const isCancellation = proQuantity === 0;
  const isBelowAssigned = proQuantity < freqData.pro.assigned;
  const isDirty = proQuantity !== freqData.pro.purchased;

  const save = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.post<TeamLicenseInventoryDTO>(
        `/admin/team/${team.id}/licenses/update`,
        {
          frequency,
          proQuantity,
          ...(reason ? { reason } : {}),
        },
      );
      refreshTeams();
      onSaved(data);
      toast({
        variant: "default",
        title: t(prefix("successTitle")),
        description: t(prefix("successDescription")),
      });
    } catch {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="space-y-5 p-6">
      <div className="space-y-1.5">
        <h2 className="text-base font-semibold leading-tight">
          {t(prefix("title"))}
        </h2>
        <p className="text-sm text-muted-foreground">{t(prefix("description"))}</p>
      </div>

      <Tabs
        value={frequency}
        onValueChange={(v) => selectFrequency(v as "monthly" | "annual")}
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="monthly">{t(prefix("monthly"))}</TabsTrigger>
          <TabsTrigger value="annual">{t(prefix("annual"))}</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="space-y-1 rounded-md border p-3 text-xs text-muted-foreground">
        <p>
          {t(prefix("summary"), {
            tier: "Pro",
            assigned: freqData.pro.assigned,
            purchased: freqData.pro.purchased,
          })}
        </p>
      </div>

      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-muted-foreground">
          {t(prefix("proLabel"))}
        </span>
        <LicenseCountSelector
          value={proQuantity}
          min={0}
          onValueChange={setProQuantity}
        />
      </div>

      {isCancellation && (
        <Alert variant="destructive">
          <AlertDescription>
            {t(prefix("cancelWarning"), {
              frequency: t(prefix(frequency)).toLowerCase(),
            })}
          </AlertDescription>
        </Alert>
      )}

      {!isCancellation && isBelowAssigned && (
        <Alert variant="warning">
          <AlertDescription>{t(prefix("reduceWarning"))}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label>{t(prefix("reasonLabel"))}</Label>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t(prefix("reasonPlaceholder"))}
          rows={2}
        />
        <p className="text-xs text-muted-foreground">
          {t(prefix("reasonHelper"))}
        </p>
      </div>

      <div className="flex justify-end">
        <ButtonLoading
          onClick={save}
          isLoading={isLoading}
          disabled={isLoading || !isDirty}
        >
          {t(prefix("save"))}
        </ButtonLoading>
      </div>
    </Card>
  );
};

export const DialogAdminTeamLicenseInventory = ({
  team,
  open,
  onClose,
}: {
  team: AdminTeamDTO;
  open: boolean;
  onClose: () => void;
}) => {
  const api = useApi();
  const toastError = useToastError();
  const { t } = useTranslation();

  const [inventory, setInventory] = useState<TeamLicenseInventoryDTO | null>(
    null,
  );
  const [users, setUsers] = useState<TeamUserDTO[]>([]);
  const [invitations, setInvitations] = useState<TeamInvitationDTO[]>([]);
  const [history, setHistory] = useState<TeamSubscriptionHistoryEntryDTO[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const pendingSetup = !team.setupCompleted;
  // Free team: set up with billing disabled. Its license stock is admin-granted
  // (monthly-only) rather than derived from a Stripe subscription. Free teams
  // still carry a Stripe customer id, so billing is the discriminator.
  const isFreePlan = team.setupCompleted && !team.billingEnabled;

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get<TeamSubscriptionHistoryEntryDTO[]>(
        `/admin/team/${team.id}/licenses/history`,
      );
      setHistory(data);
    } catch {
      toastError();
    } finally {
      setHistoryLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team.id]);

  useEffect(() => {
    if (!open) return;
    void (async () => {
      try {
        const { data } = await api.get<TeamLicenseInventoryDTO>(
          `/admin/team/${team.id}/licenses`,
        );
        setInventory(data);
      } catch {
        toastError();
      }
    })();
  }, [open, team.id]);

  // Team members and pending invitations resolve the per-seat tooltips in the
  // inventory card (which user is assigned or invited on each license).
  useEffect(() => {
    if (!open) return;
    void (async () => {
      try {
        const [usersResponse, invitationsResponse] = await Promise.all([
          api.get<AdminTeamUserDTO[]>(`/admin/team/${team.id}/users`),
          api.get<TeamInvitationDTO[]>(`/admin/team/${team.id}/invitations`),
        ]);
        setUsers(
          usersResponse.data.map((user) => ({
            ...user,
            customSearchContactsQuery: null,
          })),
        );
        setInvitations(invitationsResponse.data);
      } catch {
        setUsers([]);
        setInvitations([]);
      }
    })();
  }, [open, team.id]);

  useEffect(() => {
    if (!open) return;
    void fetchHistory();
  }, [open, fetchHistory]);

  const noop = () => {};

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[900px]">
        <DialogHeader>
          <DialogTitle>
            {t("admin.dataTableAdminTeams.dialogs.licenseInventory.title", {
              teamName: team.name,
            })}
          </DialogTitle>
        </DialogHeader>
        {inventory ? (
          <Tabs defaultValue="inventory">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="inventory">
                {t(
                  "admin.dataTableAdminTeams.dialogs.licenseInventory.tabs.inventory",
                )}
              </TabsTrigger>
              <TabsTrigger value="history">
                {t(
                  "admin.dataTableAdminTeams.dialogs.licenseInventory.tabs.history",
                )}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="inventory" className="mt-4">
              {pendingSetup ? (
                <InitialLicensesEditor team={team} onSaved={fetchHistory} />
              ) : isFreePlan ? (
                <FreePlanLicensesEditor
                  team={team}
                  inventory={inventory}
                  onSaved={(updated) => {
                    setInventory(updated);
                    void fetchHistory();
                  }}
                />
              ) : (
                <div className="space-y-4">
                  <CardLicenseInventory
                    inventory={inventory}
                    readOnly
                    users={users}
                    invitations={invitations}
                    onManageMonthly={noop}
                    onManageAnnual={noop}
                    onCancelMonthly={noop}
                    onCancelAnnual={noop}
                    onResumeMonthly={noop}
                    onResumeAnnual={noop}
                    onSwitchConfirmed={noop}
                  />
                  <QuantitiesOverrideEditor
                    team={team}
                    inventory={inventory}
                    onSaved={(updated) => {
                      setInventory(updated);
                      void fetchHistory();
                    }}
                  />
                </div>
              )}
            </TabsContent>
            <TabsContent value="history" className="mt-4">
              <CardSubscriptionHistory
                entries={history}
                prices={inventory.prices}
                loading={historyLoading}
              />
            </TabsContent>
          </Tabs>
        ) : (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {t("admin.dataTableAdminTeams.dialogs.licenseInventory.loading")}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
